import * as THREE from 'three';

export interface EditableHandle extends THREE.Mesh {
  userData: {
    nodeId: string;
    [key: string]: unknown;
  };
}

export interface PointerEditorCallbacks {
  canEdit(): boolean;
  onSelect(nodeId: string): void;
  onMove(nodeId: string, position: THREE.Vector3): void;
  onCommit(nodeId: string, position: THREE.Vector3): void;
  onEnd(changed: boolean): void;
}

export class PointerEditor {
  private readonly raycaster = new THREE.Raycaster();
  private readonly pointer = new THREE.Vector2();
  private readonly dragPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0));
  private readonly hit = new THREE.Vector3();
  private selected: EditableHandle | null = null;
  private didMove = false;

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly camera: THREE.Camera,
    private readonly handles: THREE.Group,
    private readonly callbacks: PointerEditorCallbacks,
  ) {
    canvas.addEventListener('pointerdown', this.onPointerDown, { capture: true });
    canvas.addEventListener('pointermove', this.onPointerMove);
    canvas.addEventListener('pointerup', this.onPointerUp);
    canvas.addEventListener('pointercancel', this.onPointerUp);
  }

  dispose(): void {
    this.canvas.removeEventListener('pointerdown', this.onPointerDown, true);
    this.canvas.removeEventListener('pointermove', this.onPointerMove);
    this.canvas.removeEventListener('pointerup', this.onPointerUp);
    this.canvas.removeEventListener('pointercancel', this.onPointerUp);
  }

  private setPointer(event: PointerEvent): void {
    const bounds = this.canvas.getBoundingClientRect();
    this.pointer.set(
      ((event.clientX - bounds.left) / bounds.width) * 2 - 1,
      -((event.clientY - bounds.top) / bounds.height) * 2 + 1,
    );
  }

  private readonly onPointerDown = (event: PointerEvent): void => {
    if (!this.callbacks.canEdit() || event.button !== 0) return;
    this.setPointer(event);
    this.raycaster.setFromCamera(this.pointer, this.camera);
    const intersection = this.raycaster.intersectObjects(this.handles.children, false)[0];
    if (!intersection) return;
    this.selected = intersection.object as EditableHandle;
    this.didMove = false;
    this.dragPlane.constant = -this.selected.position.y;
    this.canvas.setPointerCapture(event.pointerId);
    this.callbacks.onSelect(this.selected.userData.nodeId);
    event.preventDefault();
    event.stopImmediatePropagation();
  };

  private readonly onPointerMove = (event: PointerEvent): void => {
    if (!this.selected || !this.callbacks.canEdit()) return;
    this.setPointer(event);
    this.raycaster.setFromCamera(this.pointer, this.camera);
    if (!this.raycaster.ray.intersectPlane(this.dragPlane, this.hit)) return;
    this.selected.position.x = this.hit.x;
    this.selected.position.z = this.hit.z;
    this.didMove = true;
    this.callbacks.onMove(this.selected.userData.nodeId, this.selected.position.clone());
    event.preventDefault();
  };

  private readonly onPointerUp = (event: PointerEvent): void => {
    if (!this.selected) return;
    const released = this.selected;
    this.selected = null;
    if (this.canvas.hasPointerCapture(event.pointerId))
      this.canvas.releasePointerCapture(event.pointerId);
    const changed = this.didMove;
    if (changed) this.callbacks.onCommit(released.userData.nodeId, released.position.clone());
    this.didMove = false;
    this.callbacks.onEnd(changed);
  };
}
